import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiService } from '../../../core/services/api.service';
import { PlayQueueService } from '../../../core/services/play-queue.service';

/**
 * "By publish date" list, opened from the New row on the video home page and the
 * New Release row on the audio home page.
 *
 * Fed by GET v2/calendar-videos?month&year, whose response is
 *   { calendar: { 'YYYY-MM-DD': [ { id, slug, album_id, title, thumbnail_image, duration, type, ... } ] } }
 * with type one of video | youtube | shorts | audio. Month / year are query params
 * (one request per change); kind is filtered client side. The Day button opens a month
 * calendar popover that marks the days with rows; clicking one only scrolls the list to that
 * date, it hides nothing.
 * The host only picks the starting kind: the video home opens on Video, the audio home on
 * Audio, and the kind dropdown (All / Video / Audio / Shorts / YouTube) is there on both.
 *
 * Open state, filters and list scroll position are kept in sessionStorage per host type, so
 * coming back from the player lands on the same panel instead of a folded-away row.
 */
@Component({
  selector: 'app-publish-date-panel',
  templateUrl: './publish-date-panel.component.html',
  styleUrls: ['./publish-date-panel.component.scss']
})
export class PublishDatePanelComponent implements OnInit, OnDestroy {
  /** Kind the panel opens on: 'video' on the video home, 'audio' on the audio home. */
  @Input() type: 'video' | 'audio' = 'video';
  @Output() closed = new EventEmitter<void>();

  loading = false;
  error = false;
  month: number = new Date().getMonth() + 1;
  year: number = new Date().getFullYear();
  /** Day picked in the calendar: a jump target inside the loaded month, not a filter. */
  day: number | '' = '';
  /** Calendar grid for the loaded month: leading blanks, then one cell per day with its row count. */
  cells: { day: number | null, date: string, count: number, isToday: boolean }[] = [];
  weekdays: string[] = [];
  /** Month calendar popover under the Day button. */
  showCalendar = false;
  /** Which rows to show. Matches the API's row `type`, plus 'all'. */
  kind: 'all' | 'video' | 'audio' | 'shorts' | 'youtube' = 'video';
  /** Kind options, labels as translation keys (the template pipes them through translate). */
  kinds: { value: 'all' | 'video' | 'audio' | 'shorts' | 'youtube', label: string }[] = [
    { value: 'all', label: 'HOME.calendar_all_types' },
    { value: 'video', label: 'HOME.calendar_video' },
    { value: 'audio', label: 'HOME.calendar_audio' },
    { value: 'shorts', label: 'HOME.calendar_shorts' },
    { value: 'youtube', label: 'HOME.calendar_youtube' }
  ];
  /** Which of the three filter dropdowns is open; only one at a time. */
  openDrop: 'month' | 'year' | 'kind' | null = null;
  months: { value: number, label: string }[] = [];
  years: number[] = [];
  groups: { date: string, label: string, items: any[] }[] = [];
  private raw: { [date: string]: any[] } = {};
  private sub: Subscription;
  /** List scroll offset to put back once the restored month has rendered. */
  private pendingScrollTop = 0;
  /** True from restoreState() until that month has loaded, so fetch() keeps the picked day. */
  private restoring = false;

  private static readonly STATE_KEY = 'publishDatePanel:';

  /** Host helper: was this panel open when the page was left? */
  static wasOpen(type: string): boolean {
    const state = PublishDatePanelComponent.readState(type);
    return !!(state && state.open);
  }

  /** Host helper: record the host's toggle so a closed panel stays closed on return. */
  static setOpen(type: string, open: boolean) {
    PublishDatePanelComponent.writeState(type, { open: open });
  }

  private static readState(type: string): any {
    try {
      const stored = sessionStorage.getItem(PublishDatePanelComponent.STATE_KEY + type);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }

  private static writeState(type: string, patch: any) {
    try {
      const state = Object.assign({}, PublishDatePanelComponent.readState(type) || {}, patch);
      sessionStorage.setItem(PublishDatePanelComponent.STATE_KEY + type, JSON.stringify(state));
    } catch (e) { /* storage blocked: the panel still works, it just will not remember */ }
  }

  constructor(
    private apiService: ApiService,
    private router: Router,
    private host: ElementRef,
    private playQueue: PlayQueueService
  ) { }

  ngOnInit() {
    this.kind = this.type === 'audio' ? 'audio' : 'video';
    this.restoreState();
    this.saveState({ open: true });
    this.buildOptions();
    this.fetch();
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    const list = (this.host.nativeElement as HTMLElement).querySelector('.pdp-list-col') as HTMLElement;
    this.saveState({ scrollTop: list ? list.scrollTop : 0 });
  }

  close() {
    this.saveState({ open: false });
    this.closed.emit();
  }

  private restoreState() {
    const state = PublishDatePanelComponent.readState(this.type);
    if (!state) {
      return;
    }
    this.restoring = true;
    const now = new Date();
    if (state.month >= 1 && state.month <= 12) {
      this.month = state.month;
    }
    if (state.year >= PublishDatePanelComponent.MIN_YEAR && state.year <= now.getFullYear()) {
      this.year = state.year;
    }
    if (['all', 'video', 'audio', 'shorts', 'youtube'].indexOf(state.kind) !== -1) {
      this.kind = state.kind;
    }
    this.pendingScrollTop = state.scrollTop > 0 ? state.scrollTop : 0;
    // The picked day is put back after the month has loaded, see fetch().
    this.day = state.day >= 1 && state.day <= 31 ? state.day : '';
  }

  private saveState(patch: any) {
    PublishDatePanelComponent.writeState(this.type, Object.assign({
      month: this.month,
      year: this.year,
      kind: this.kind,
      day: this.day
    }, patch));
  }

  /** After a restored month renders: back to where the list was scrolled to. */
  private restoreScroll() {
    this.restoring = false;
    const top = this.pendingScrollTop;
    this.pendingScrollTop = 0;
    if (!top) {
      return;
    }
    setTimeout(() => {
      const list = (this.host.nativeElement as HTMLElement).querySelector('.pdp-list-col') as HTMLElement;
      if (list) {
        list.scrollTop = top;
      }
    });
  }

  toggleCalendar(event?: Event) {
    if (event) { event.stopPropagation(); }
    this.openDrop = null;
    this.showCalendar = !this.showCalendar;
  }

  /** Filter dropdown clicked: open it and fold away the calendar / the other dropdown. */
  toggleDrop(name: 'month' | 'year' | 'kind', event?: Event) {
    if (event) { event.stopPropagation(); }
    this.showCalendar = false;
    this.openDrop = this.openDrop === name ? null : name;
  }

  selectMonth(value: number) {
    this.openDrop = null;
    if (value === this.month) { return; }
    this.month = value;
    this.fetch();
  }

  selectYear(value: number) {
    this.openDrop = null;
    if (value === this.year) { return; }
    this.year = value;
    this.fetch();
  }

  selectKind(value: 'all' | 'video' | 'audio' | 'shorts' | 'youtube') {
    this.openDrop = null;
    if (value === this.kind) { return; }
    this.kind = value;
    this.onKindChange();
  }

  /** Translation key of the selected kind, for the closed dropdown button. */
  kindLabel(): string {
    const k = this.kinds.find(x => x.value === this.kind);
    return k ? k.label : '';
  }

  /** Escape folds whatever popover is open. */
  @HostListener('document:keydown.escape')
  onEscape() {
    this.openDrop = null;
    this.showCalendar = false;
  }

  /** Any click outside the Day button / popover folds the calendar away. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const root = this.host.nativeElement as HTMLElement;
    if (this.showCalendar) {
      const wrap = root.querySelector('.pdp-day-wrap');
      if (wrap && !wrap.contains(event.target as Node)) {
        this.showCalendar = false;
      }
    }
    if (this.openDrop) {
      const drop = root.querySelector('.pdp-drop.open');
      if (drop && !drop.contains(event.target as Node)) {
        this.openDrop = null;
      }
    }
  }

  monthLabel(): string {
    const m = this.months.find(x => x.value === this.month);
    return m ? m.label : '';
  }

  private locale(): string {
    const lang = localStorage.getItem('currentLanguage') || 'en';
    return lang === 'hi' ? 'hi-IN' : lang;
  }

  private buildOptions() {
    const locale = this.locale();
    this.months = [];
    for (let m = 0; m < 12; m++) {
      let label: string;
      try {
        label = new Date(2000, m, 1).toLocaleDateString(locale, { month: 'long' });
      } catch (e) {
        label = new Date(2000, m, 1).toLocaleDateString('en', { month: 'long' });
      }
      this.months.push({ value: m + 1, label: label });
    }
    const thisYear = new Date().getFullYear();
    this.years = [];
    for (let y = thisYear; y >= PublishDatePanelComponent.MIN_YEAR; y--) {
      this.years.push(y);
    }
    // Week starts on Sunday; 2000-01-02 was a Sunday.
    this.weekdays = [];
    for (let d = 0; d < 7; d++) {
      try {
        this.weekdays.push(new Date(2000, 0, 2 + d).toLocaleDateString(locale, { weekday: 'short' }));
      } catch (e) {
        this.weekdays.push(new Date(2000, 0, 2 + d).toLocaleDateString('en', { weekday: 'short' }));
      }
    }
  }

  private static readonly MIN_YEAR = 2000;

  canPrevMonth(): boolean {
    return !(this.year === PublishDatePanelComponent.MIN_YEAR && this.month === 1);
  }

  canNextMonth(): boolean {
    const now = new Date();
    return !(this.year === now.getFullYear() && this.month === 12) && this.year <= now.getFullYear();
  }

  prevMonth() {
    if (!this.canPrevMonth()) { return; }
    if (this.month === 1) { this.month = 12; this.year--; } else { this.month--; }
    this.fetch();
  }

  nextMonth() {
    if (!this.canNextMonth()) { return; }
    if (this.month === 12) { this.month = 1; this.year++; } else { this.month++; }
    this.fetch();
  }

  goToday() {
    const now = new Date();
    if (this.month !== now.getMonth() + 1 || this.year !== now.getFullYear()) {
      this.month = now.getMonth() + 1;
      this.year = now.getFullYear();
      this.fetch();
    }
  }

  /** One cell per day of the loaded month, counting the rows the current kind shows on it. */
  private buildCells() {
    const counts: { [date: string]: number } = {};
    this.groups.forEach(g => { counts[g.date] = g.items.length; });
    const now = new Date();
    const todayKey = this.dateKey(now.getFullYear(), now.getMonth() + 1, now.getDate());
    const first = new Date(this.year, this.month - 1, 1);
    const daysInMonth = new Date(this.year, this.month, 0).getDate();
    const cells = [];
    for (let i = 0; i < first.getDay(); i++) {
      cells.push({ day: null, date: '', count: 0, isToday: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const key = this.dateKey(this.year, this.month, d);
      cells.push({ day: d, date: key, count: counts[key] || 0, isToday: key === todayKey });
    }
    this.cells = cells;
  }

  private dateKey(y: number, m: number, d: number): string {
    return y + '-' + (m < 10 ? '0' + m : m) + '-' + (d < 10 ? '0' + d : d);
  }

  /** Month / year changed: a new API round-trip. The picked day is dropped because it belongs to the old month. */
  fetch() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    // Keep the day only while restoring: it was picked in the month being loaded.
    if (!this.restoring) {
      this.day = '';
    }
    this.saveState({});
    this.loading = true;
    this.error = false;
    this.raw = {};
    this.groups = [];
    this.buildCells();
    this.sub = this.apiService
      .callGetAPI('v2/calendar-videos?month=' + this.month + '&year=' + this.year)
      .subscribe(
        data => {
          this.loading = false;
          const calendar = data && data['response'] ? data['response'].calendar : null;
          // An empty month comes back as [] (PHP empty array), a filled one as an object keyed by date.
          this.raw = calendar && !Array.isArray(calendar) ? calendar : {};
          this.applyFilters();
          this.restoreScroll();
        },
        error => {
          this.loading = false;
          this.error = true;
          this.restoring = false;
          this.apiService.failureResponse(error);
        });
  }

  /** Does this row belong on the page (type) and in the selected kind? */
  private matches(item): boolean {
    return this.kind === 'all' || item.type === this.kind;
  }

  /** Kind changed: the marked days belong to the kind too, so drop the picked day. */
  onKindChange() {
    this.day = '';
    this.applyFilters();
    this.saveState({});
  }

  /** Calendar day clicked: remember it and scroll the list to that date. */
  selectDay(cell) {
    if (!cell || !cell.day || cell.count === 0) { return; }
    this.day = cell.day;
    this.showCalendar = false;
    this.saveState({});
    this.onDayChange();
  }

  /** Kind changed (or month loaded): re-group the month without another request. */
  applyFilters() {
    const groups = [];
    Object.keys(this.raw).sort().forEach(date => {
      const items = (this.raw[date] || [])
        .filter(item => this.matches(item))
        .map(item => {
          // description is stored as editor HTML (<p>…</p>); the row shows it as one line of text.
          if (item.descText === undefined) {
            item.descText = (item.description || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
          }
          return item;
        });
      if (items.length > 0) {
        groups.push({ date: date, label: this.dateLabel(date), items: items });
      }
    });
    this.groups = groups;
    this.buildCells();
  }

  /** Element id of a date group, so the day dropdown can scroll to it. */
  groupId(date: string): string {
    return 'pdp-day-' + date;
  }

  isSelectedDay(date: string): boolean {
    return this.day !== '' && parseInt(date.substr(8, 2), 10) === Number(this.day);
  }

  /**
   * Day picked: scroll that date's heading into view and leave every other date in place.
   * The list scrolls inside .pdp-list-col on desktop; on phones that column has no max-height,
   * so the page itself scrolls and the fixed header has to be cleared.
   */
  onDayChange() {
    if (this.day === '') {
      return;
    }
    const group = this.groups.find(g => this.isSelectedDay(g.date));
    if (!group) {
      return;
    }
    setTimeout(() => {
      const root: HTMLElement = this.host.nativeElement;
      const target = root.querySelector('#' + this.groupId(group.date)) as HTMLElement;
      const body = root.querySelector('.pdp-list-col') as HTMLElement;
      if (!target || !body) {
        return;
      }
      if (body.scrollHeight > body.clientHeight + 1) {
        const top = target.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - 6;
        body.scrollTo({ top: top, behavior: 'smooth' });
      } else {
        const headerOffset = 90;
        window.scrollTo({ top: target.getBoundingClientRect().top + window.pageYOffset - headerOffset, behavior: 'smooth' });
      }
    });
  }

  /** 'YYYY-MM-DD' -> 'Fri, 11 September 2026' in the current language. */
  private dateLabel(date: string): string {
    const parts = date.split('-').map(Number);
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    const options = { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' };
    try {
      return d.toLocaleDateString(this.locale(), options);
    } catch (e) {
      return d.toLocaleDateString('en', options);
    }
  }

  thumb(item) {
    return item && item.thumbnail_image ? item.thumbnail_image : environment.defaultImage;
  }

  trackGroup(index, group) {
    return group.date;
  }

  trackItem(index, item) {
    return item.type + '-' + item.id;
  }

  open(item) {
    if (item.type === 'audio') {
      // Single track page: /audio/:id?albumId=… (same shape albums / audios pages navigate with).
      const queryParams = item.album_id ? { albumId: item.album_id } : {};
      this.router.navigate(['/audio', item.id], { queryParams: queryParams });
      return;
    }
    this.playSequenceFrom(item);
  }

  /**
   * A video click plays it and then everything listed after it (this month, current type,
   * audios skipped), in order, without the user touching anything. The list is stored as a
   * play queue under an ad-hoc playlist id and the players auto-advance through it, shorts
   * handing over to the shorts feed and back; the queue's autoPlay flag keeps the sequence
   * moving even for an account whose auto_play setting is off.
   */
  private playSequenceFrom(item) {
    const videos = [];
    this.groups.forEach(g => g.items.forEach(v => {
      if (v.type !== 'audio' && (v.slug || v.id)) {
        videos.push(v);
      }
    }));
    const start = videos.findIndex(v => v === item);
    const sequence = (start === -1 ? [item] : videos.slice(start)).map(v => ({
      slug: v.slug ? String(v.slug) : String(v.id),
      title: v.title,
      thumbnail_image: v.thumbnail_image,
      video_duration: v.duration,
      is_shorts: v.type === 'shorts' ? 1 : 0
    }));

    // Not a real playlist id: the players only use it to find this queue in storage, and
    // the detail endpoint answers an unknown id with an empty related list, not an error.
    const playlistId = 'by-date-' + this.year + '-' + (this.month < 10 ? '0' + this.month : this.month);
    const playlistName = this.monthLabel() + ' ' + this.year;
    const queue = this.playQueue.save(playlistId, playlistName, sequence, null, true);

    const first = sequence[0];
    if (!queue || sequence.length === 1) {
      // Nothing after it: plain single play, no playlist context to carry.
      this.router.navigate([(first.is_shorts ? 'shorts/' : 'watch/') + first.slug]);
      return;
    }
    this.router.navigateByUrl(this.playQueue.pathFor(first, queue.playlistId, queue.playlistName));
  }
}
